/**
 * Cart drawer add-ons for Horizon:
 *  - one-time cart deals   [data-xcart-add]
 *  - package protection    [data-xcart-protect] / [data-xcart-protect-toggle]
 *
 * Design principles (v2 — hardened):
 *  - THE LIVE CART IS THE ONLY SOURCE OF TRUTH. Before any protection change we
 *    fetch /cart.js and act on what's actually in the cart — never on what the
 *    (possibly stale) DOM shows. Toggling any number of times is idempotent.
 *  - Protection is changed via /cart/update.js keyed by VARIANT ID (the Shrine
 *    approach), never by line key — line keys can go stale, variant ids can't.
 *    This also self-heals broken states (e.g. protection at quantity 2 → 1).
 *  - Listeners are delegated at the document level so they survive morphs and
 *    full section swaps done by PDP scripts.
 *  - Automatic actions (auto-add, cleanup, resync) are silent and never run
 *    inside the Shopify theme editor; only direct user clicks may alert.
 */

const SECTION_ID = 'cart-drawer-section';
const PROTECT_OFF_KEY = 'xcartProtectOff';
const PROTECT_AUTO_KEY = 'xcartProtectAutoAdded';

// Build marker — check with `window.__xcartVersion` in the console.
window.__xcartVersion = 'v2026-07-30.10';

/**
 * Mutual exclusion for cart mutations.
 *
 * A plain boolean was not enough: several paths release the lock early and then
 * `await` a resync, so their own `finally` would later clear a lock that a
 * NEWER action had since taken — letting two mutations and two morphs run at
 * once. Each action takes a token; only the current token owner may release.
 */
let inFlight = false;
let inFlightToken = 0;

function beginFlight() {
  inFlight = true;
  inFlightToken += 1;
  return inFlightToken;
}

function endFlight(token) {
  if (token === inFlightToken) inFlight = false;
}

/* ---------------------------------------------------------------- helpers */

function routes() {
  return (window.Theme && window.Theme.routes) || {};
}

function inDesignMode() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

/* sessionStorage can throw (e.g. some private-browsing modes) — never let
   that break the toggle; fall back to in-memory state for the session. */
const memStore = {};
function storeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return memStore[key] ?? null;
  }
}
function storeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    memStore[key] = value;
  }
}
function storeRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    delete memStore[key];
  }
}

async function post(url, payload, attempt = 0) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => null);
  // 409 = cart token conflict. Retry only IDEMPOTENT calls (change/update).
  // Never auto-retry /cart/add — if the first request did land server-side,
  // a retry would silently add the item twice.
  const isAdd = /\/cart\/add/.test(String(url));
  if (res.status === 409 && attempt < 1 && !isAdd) {
    await new Promise((r) => setTimeout(r, 350));
    return post(url, payload, attempt + 1);
  }
  return { ok: res.ok, status: res.status, data };
}

/** The live cart, straight from Shopify — never from any cache layer. */
async function fetchCart() {
  const res = await fetch('/cart.js?t=' + Date.now(), {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
  return res.json();
}

/** Quantity of a variant currently in the live cart. */
function variantQty(cart, variantId) {
  let qty = 0;
  for (const item of cart.items) {
    if (item.id === variantId || item.variant_id === variantId) qty += item.quantity;
  }
  return qty;
}

/** Item count parsed out of the freshly rendered drawer section. */
function itemCountFrom(sections) {
  const html = sections && sections[SECTION_ID];
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const count = doc.querySelector('[ref="cartItemCount"]')?.textContent;
  return count ? parseInt(count, 10) : null;
}

/** Morph the drawer section in place; fall back to a manual inner swap. */
async function applySections(sections) {
  const html = sections && sections[SECTION_ID];
  if (!html) return;
  // Inside the theme editor the section-renderer's morph can silently no-op
  // (the editor manages sections itself) — swap the DOM directly there.
  if (inDesignMode()) {
    manualSwap(html);
    return;
  }
  try {
    const { morphSection } = await import('@theme/section-renderer');
    await morphSection(SECTION_ID, html, { mode: 'hydration' });
  } catch (e) {
    manualSwap(html);
  }
}

function manualSwap(html) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.cart-drawer__inner');
    const current = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (fresh && current) current.replaceWith(fresh);
  } catch (e) {
    /* last resort: leave DOM as-is */
  }
}

/** Re-render the drawer from the server (fixes any stale DOM).
    Cache-busted + no-store: page-speed/caching apps must never serve a stale
    (e.g. empty-cart) copy of this section, or the drawer flips to wrong state. */
let lastResync = 0;
async function resyncDrawer(force = false) {
  const now = Date.now();
  if (!force && now - lastResync < 2000) return; // throttle: never loop re-renders
  lastResync = now;
  try {
    const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}&t=${now}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return;
    const sections = await res.json();
    await applySections(sections);
  } catch (e) {
    /* non-fatal */
  }
}

/**
 * Notify Horizon (cart bubble etc.). Falls back to the generic cart:update event.
 *
 * Payload shape matters (see assets/standard-events.d.ts):
 *  - action 'add' lines take `merchandiseId`, not `id`
 *  - `lines` must not be empty (@minItems 1)
 *  - `cart: undefined` makes header-actions.js skip its screen-reader
 *    announcement, so pass null
 */
async function dispatchCartUpdate(action, lines, sections) {
  const itemCount = itemCountFrom(sections) ?? 0;
  const safeLines = (Array.isArray(lines) ? lines : [])
    .filter(Boolean)
    .map((line) =>
      action === 'add' && line.merchandiseId === undefined && line.id !== undefined
        ? { merchandiseId: String(line.id), quantity: line.quantity }
        : line
    );
  if (safeLines.length === 0) {
    safeLines.push(action === 'add' ? { merchandiseId: '0', quantity: 0 } : { id: '0', quantity: 0 });
  }
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: safeLines,
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: null,
      detail: { itemCount, source: 'cart-addons', didError: false, sections },
    });
  } catch (e) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount, source: 'cart-addons' } },
      })
    );
  }
}

function setBusy(el, busy) {
  if (el) el.setAttribute('aria-busy', busy ? 'true' : 'false');
}

/* ------------------------------------------------------------------ deals */

async function addDeal(variantId, busyEl, slot) {
  if (inFlight) return;
  if (inDesignMode()) {
    console.info('[xcart] theme editor: deal click ignored (would mutate the live cart)');
    return;
  }
  if (!variantId) {
    alert(
      'This add-on has no purchasable variant. Check the product in Shopify admin: untrack inventory or enable "Continue selling when out of stock".'
    );
    return;
  }
  const token = beginFlight();
  setBusy(busyEl, true);
  try {
    const vid = Number(variantId);

    // Snapshot the live quantity BEFORE adding, so we can verify the add
    // landed as exactly +1 afterwards.
    let preQty = 0;
    try {
      preQty = variantQty(await fetchCart(), vid);
    } catch (e) { /* proceed without snapshot */ }

    const item = { id: vid, quantity: 1 };
    // Tag the line with its deal slot (hidden property, invisible to the
    // customer) so each slot hides/reappears independently — even when two
    // slots point at the same product.
    if (slot) item.properties = { _xcart_slot: String(slot) };

    const { ok, data } = await post(routes().cart_add_url || '/cart/add.js', {
      items: [item],
      sections: SECTION_ID,
      sections_url: window.location.pathname,
    });
    if (!ok) {
      console.warn('[cart-addons] deal add failed:', data);
      alert('Could not add to cart: ' + ((data && (data.description || data.message)) || 'unknown error'));
      return;
    }

    // VERIFY & SELF-CORRECT: whatever else touches the cart (another script,
    // an app, a race), the net effect of one click must be exactly +1.
    let finalSections = data && data.sections;
    try {
      const after = await fetchCart();
      const postQty = variantQty(after, vid);
      const excess = postQty - preQty - 1;
      if (excess > 0) {
        console.warn(
          '[xcart] double add detected: variant', vid, 'went', preQty, '→', postQty,
          '(expected', preQty + 1 + '). Auto-correcting.'
        );
        // Reduce the slot's own line (fresh key — just fetched, can't be stale).
        const line =
          (slot && after.items.find((i) => String((i.properties || {})['_xcart_slot']) === String(slot))) ||
          after.items.find((i) => (i.id === vid || i.variant_id === vid));
        if (line && line.quantity > 1) {
          const fix = await post(routes().cart_change_url || '/cart/change.js', {
            id: line.key,
            quantity: line.quantity - excess,
            sections: SECTION_ID,
            sections_url: window.location.pathname,
          });
          if (fix.ok && fix.data && fix.data.sections) finalSections = fix.data.sections;
        }
      }
    } catch (e) {
      console.warn('[xcart] post-add verification skipped:', e);
    }

    await applySections(finalSections);
    await dispatchCartUpdate('add', [{ merchandiseId: String(vid), quantity: 1 }], finalSections);
  } catch (e) {
    console.warn('[cart-addons] deal add error:', e);
  } finally {
    endFlight(token);
    setBusy(busyEl, false);
  }
}

/* -------------------------------------------------- subscription upgrade */

/** True when this cart line was added by the drawer's deal slots. */
function isDealLine(item) {
  return Boolean(item.properties && item.properties['_xcart_slot']);
}

/**
 * Units of `variantId` currently in the cart on selling plan `planId`.
 *
 * A boolean "is it there?" is NOT safe for verifying an upgrade: the same
 * variant can already be in the cart on that plan as a separate line (customer
 * added both a one-time and a subscription), in which case a boolean returns
 * true BEFORE the request is even sent — and every failure would be misread as
 * success. Compare this count before and after instead.
 */
function planQty(cart, variantId, planId) {
  let qty = 0;
  for (const i of cart.items) {
    const plan = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
    if (
      (i.id === variantId || i.variant_id === variantId) &&
      plan &&
      Number(plan.id) === Number(planId)
    ) {
      qty += i.quantity;
    }
  }
  return qty;
}

/**
 * The selling-plan id currently attached to a cart line, or 0 for a one-time line.
 *
 * Used to match the line the button was rendered against. A blanket
 * "!i.selling_plan_allocation" test is NOT enough once the ladder is in play:
 * rung 2 upgrades a line that ALREADY has a plan, and that test would find
 * nothing and silently bail. Matching on the exact plan the button expects is
 * also stricter than the old test for one-time lines (fromPlan === 0), so this
 * is a tightening everywhere, not a loosening.
 */
function linePlanId(item) {
  const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
  return plan ? Number(plan.id) : 0;
}

/** Did the upgrade actually land, despite the request reporting an error? */
async function upgradeLanded(vid, planId, before) {
  try {
    return planQty(await fetchCart(), vid, planId) > before;
  } catch (e) {
    return false; // genuinely offline — tell the customer
  }
}

/** Polite screen-reader announcement (the morph destroys the focused button). */
function announce(message) {
  let region = document.getElementById('xcart-live');
  if (!region) {
    region = document.createElement('div');
    region.id = 'xcart-live';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.style.cssText =
      'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
    document.body.appendChild(region);
  }
  region.textContent = '';
  setTimeout(() => {
    region.textContent = message;
  }, 50);
}

/** Keep keyboard users inside the drawer after a morph removes their button. */
function restoreDrawerFocus() {
  const active = document.activeElement;
  if (active && active !== document.body && document.contains(active)) return;
  const drawer =
    document.querySelector('#cart-drawer .theme-drawer__title') ||
    document.querySelector('#cart-drawer .cart-drawer__inner');
  if (!drawer) return;
  if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
  try {
    drawer.focus({ preventScroll: true });
  } catch (e) {
    /* non-fatal */
  }
}

/**
 * Convert a one-time cart line to its subscription selling plan.
 *
 * Live-cart driven: the button's line key may be stale after a morph, so we
 * always re-locate the line in the freshly fetched cart — by key first, then by
 * variant id among lines that have NO selling plan AND are not deal add-ons
 * (a deal slot could hold the same variant; upgrading that one would be wrong).
 *
 * The line KEY changes when Shopify re-creates the line with a plan attached,
 * so a 409 retry inside post() can come back as an error for a change that
 * actually succeeded. Every failure path therefore re-checks the live cart
 * before it alerts the customer.
 */
async function upgradeToSubscription(btn) {
  if (inFlight) return;
  if (inDesignMode()) {
    console.info('[xcart] theme editor: upgrade click ignored (would mutate the live cart)');
    return;
  }
  const planId = Number(btn.dataset.sellingPlan);
  const vid = Number(btn.dataset.variantId);
  // Which plan the line is on RIGHT NOW: 0 for a one-time line, or the current
  // plan id when this is a ladder step (monthly -> 3-month).
  const fromPlan = Number(btn.dataset.fromPlan) || 0;
  // Quantity scaling for this rung, as a ratio rather than a fixed number: a
  // plan that delivers every 3 months usually means "send 3 bottles". Reading
  // it as mult/div lets the target be recomputed from the LIVE line below, so
  // a quantity the customer changed after this button rendered is respected
  // instead of being silently overwritten with a stale one. Both default to 1.
  const qtyMult = Number(btn.dataset.qtyMult) || 1;
  const qtyDiv = Number(btn.dataset.qtyDiv) || 1;
  if (!planId || !vid) return;
  // Nothing to do — and posting it would churn the line key for no reason.
  if (planId === fromPlan) return;
  const doneMsg = fromPlan ? 'Subscription plan updated.' : 'Switched to a subscription.';

  const token = beginFlight();
  setBusy(btn, true);
  let planBefore = 0;
  // Only a request that was actually ISSUED can have "landed". Without this,
  // a cart fetch that throws before the POST would run the delta check with
  // planBefore still 0 and read a pre-existing subscription line as success.
  let sent = false;
  try {
    const cart = await fetchCart();
    planBefore = planQty(cart, vid, planId);
    const key = btn.dataset.lineKey;
    const line =
      cart.items.find((i) => i.key === key && linePlanId(i) === fromPlan) ||
      cart.items.find(
        (i) =>
          (i.id === vid || i.variant_id === vid) &&
          linePlanId(i) === fromPlan &&
          !isDealLine(i)
      );

    if (!line) {
      // Already upgraded, or the line is gone — the button is stale.
      // Force the resync past the throttle so the drawer can't stay wrong.
      endFlight(token);
      setBusy(btn, false);
      await resyncDrawer(true);
      restoreDrawerFocus();
      return;
    }

    const targetQty = Math.max(1, Math.round((line.quantity * qtyMult) / qtyDiv));

    const payload = {
      id: line.key,
      quantity: targetQty,
      selling_plan: planId,
      sections: SECTION_ID,
      sections_url: window.location.pathname,
    };
    // change.js re-creates the line, so carry its properties across.
    if (line.properties && Object.keys(line.properties).length > 0) {
      payload.properties = line.properties;
    }

    sent = true;
    const resp = await post(routes().cart_change_url || '/cart/change.js', payload);

    if (!resp.ok) {
      // Did it actually land anyway? (409 retry, racing script, stale key.)
      if (await upgradeLanded(vid, planId, planBefore)) {
        console.info('[xcart] upgrade reported an error but the cart is correct — resyncing');
        endFlight(token);
        setBusy(btn, false);
        await resyncDrawer(true);
        announce(doneMsg);
        restoreDrawerFocus();
        return;
      }
      console.warn('[cart-addons] subscription upgrade failed:', resp.data);
      alert(
        'Could not switch this item to a subscription: ' +
          ((resp.data && (resp.data.description || resp.data.message)) || 'unknown error')
      );
      return;
    }

    // post() returns data:null when the body is not JSON — never assume sections.
    if (resp.data && resp.data.sections) {
      await applySections(resp.data.sections);
      await dispatchCartUpdate(
        'update',
        [{ id: line.key, quantity: targetQty }],
        resp.data.sections
      );
    } else {
      await resyncDrawer(true);
    }
    announce(doneMsg);
    restoreDrawerFocus();
  } catch (e) {
    // Network blip: the change may still have landed. Check before alarming.
    if (sent && (await upgradeLanded(vid, planId, planBefore))) {
      endFlight(token);
      setBusy(btn, false);
      await resyncDrawer(true);
      announce(doneMsg);
      restoreDrawerFocus();
      return;
    }
    console.warn('[cart-addons] subscription upgrade error:', e);
    alert('Could not reach the cart. Please refresh the page and try again.');
  } finally {
    endFlight(token);
    setBusy(btn, false);
  }
}

/* ------------------------------------------------------------- protection */

function protectRoot() {
  return document.querySelector('[data-xcart-protect]');
}

function protectVariantId(root) {
  const vid = parseInt(root?.dataset.variantId || '', 10);
  return Number.isFinite(vid) && vid > 0 ? vid : null;
}

/**
 * Drive protection to the desired state, based on the LIVE cart.
 * - desiredOn=true  → ensure exactly quantity 1 in the cart
 * - desiredOn=false → ensure quantity 0
 * Idempotent: safe to call any number of times, from any (stale) UI state.
 * @param {boolean} desiredOn
 * @param {{silent?: boolean, cart?: object, busyEl?: Element}} [opts]
 */
async function applyProtection(desiredOn, opts = {}) {
  const { silent = false, busyEl = null } = opts;
  const root = protectRoot();
  const vid = protectVariantId(root);
  if (!root || inFlight) return;
  if (!vid) {
    if (!silent) {
      alert(
        'The protection product has no purchasable variant. In Shopify admin: untrack its inventory or enable "Continue selling when out of stock".'
      );
    }
    return;
  }

  const token = beginFlight();
  const toggle = busyEl || root.querySelector('[data-xcart-protect-toggle]');
  setBusy(toggle, true);

  try {
    const cart = opts.cart || (await fetchCart());
    const qty = variantQty(cart, vid);
    // 'add' only when a line is truly created — normalizations dispatch
    // 'update' so the drawer's auto-open never triggers from background fixes.
    const eventAction = desiredOn ? (qty === 0 ? 'add' : 'update') : 'remove';

    // Already in the desired state → just make sure the UI reflects reality.
    if ((desiredOn && qty === 1) || (!desiredOn && qty === 0)) {
      endFlight(token); // release before resync (it performs no cart mutation)
      setBusy(toggle, false);
      await resyncDrawer();
      return;
    }

    let resp;
    if (desiredOn && qty === 0) {
      resp = await post(routes().cart_add_url || '/cart/add.js', {
        items: [{ id: vid, quantity: 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      });
    } else {
      // Turning off, or normalizing a broken quantity (e.g. 2 → 1):
      // variant-keyed update — no line keys, can't go stale.
      resp = await post('/cart/update.js', {
        updates: { [vid]: desiredOn ? 1 : 0 },
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      });
    }

    if (!resp.ok) {
      console.warn('[cart-addons] protection update failed:', resp.data);
      if (!silent) {
        alert(
          'Could not update Package Protection: ' +
            ((resp.data && (resp.data.description || resp.data.message)) || 'unknown error')
        );
      }
      return;
    }

    // post() returns data:null when the body is not JSON — never assume sections.
    if (resp.data && resp.data.sections) {
      await applySections(resp.data.sections);
      await dispatchCartUpdate(
        eventAction,
        [{ id: String(vid), quantity: desiredOn ? 1 : 0 }],
        resp.data.sections
      );
    } else {
      await resyncDrawer(true);
    }
  } catch (e) {
    console.warn('[cart-addons] protection error:', e);
    if (!silent) alert('Could not reach the cart. Please refresh the page and try again.');
  } finally {
    endFlight(token);
    setBusy(toggle, false);
  }
}

/** User clicked the switch: flip relative to the LIVE cart, not the UI. */
async function onProtectToggle(toggleEl) {
  if (inFlight) return;
  if (inDesignMode()) {
    console.info('[xcart] theme editor: protection toggle ignored (would mutate the live cart)');
    return;
  }
  const root = toggleEl.closest('[data-xcart-protect]');
  const vid = protectVariantId(root);
  if (!root) return;
  if (!vid) {
    alert(
      'The protection product has no purchasable variant. In Shopify admin: untrack its inventory or enable "Continue selling when out of stock".'
    );
    return;
  }

  setBusy(toggleEl, true);
  let cart = null;
  try {
    cart = await fetchCart();
  } catch (e) {
    setBusy(toggleEl, false);
    alert('Could not reach the cart. Please refresh the page and try again.');
    return;
  }
  setBusy(toggleEl, false);

  const currentlyOn = variantQty(cart, vid) > 0;
  const desiredOn = !currentlyOn;

  // Remember the customer's explicit choice for this session.
  if (desiredOn) {
    storeRemove(PROTECT_OFF_KEY);
  } else {
    storeSet(PROTECT_OFF_KEY, '1');
  }

  await applyProtection(desiredOn, { cart });
}

/**
 * Automatic reconciliation — runs on drawer open and after cart updates.
 * All decisions are made against the LIVE cart. Never runs in the theme editor,
 * never alerts.
 */
async function reconcileProtection(opts = {}) {
  // autoAdd: only true when the drawer is (being) opened — never on plain
  // page loads, so background reconciles can't pop the drawer open.
  const autoAdd = opts.autoAdd !== false;
  if (inDesignMode() || inFlight) return;

  // Cart is empty → re-arm the defaults so the NEXT cart starts fresh with
  // protection pre-selected again (auto-add is once per CART, not per session).
  if (document.querySelector('#cart-drawer .cart-drawer--empty')) {
    storeRemove(PROTECT_AUTO_KEY);
    storeRemove(PROTECT_OFF_KEY);
    return;
  }
  const root = protectRoot();
  const vid = protectVariantId(root);
  if (!root || !vid) return;

  let cart;
  try {
    cart = await fetchCart();
  } catch (e) {
    return;
  }
  if (inFlight) return; // something started while we were fetching

  const qty = variantQty(cart, vid);
  const otherItems = cart.item_count - qty;

  // Quantity of deal/upsell lines (every drawer-added deal carries the
  // hidden _xcart_slot property).
  let dealQty = 0;
  for (const item of cart.items) {
    if (item.properties && item.properties['_xcart_slot']) dealQty += item.quantity;
  }
  const realItems = cart.item_count - qty - dealQty;

  // ANTI-ABUSE: if only addons remain (deals at upsell pricing and/or
  // protection) with no real product, empty the cart entirely — upsell
  // prices must not be purchasable on their own. Runs on every check:
  // an addons-only cart is never a legitimate mid-shopping state (deals
  // and protection can only ever be added alongside real items).
  if (cart.item_count > 0 && realItems === 0) {
    console.info('[xcart] addons-only cart detected — clearing');
    const clearedLines = cart.items.map((i) => ({ id: i.key, quantity: 0 }));
    const clearToken = beginFlight();
    try {
      const resp = await post('/cart/clear.js', {
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      });
      if (resp.ok) {
        await applySections(resp.data && resp.data.sections);
        await dispatchCartUpdate('remove', clearedLines, resp.data && resp.data.sections);
        // Fresh start for the next cart: protection pre-selects again.
        storeRemove(PROTECT_AUTO_KEY);
        storeRemove(PROTECT_OFF_KEY);
      }
    } catch (e) {
      console.warn('[cart-addons] cart clear failed:', e);
    } finally {
      endFlight(clearToken);
    }
    return;
  }

  // Broken state (e.g. added twice) → normalize to exactly 1.
  if (qty > 1) {
    await applyProtection(true, { silent: true, cart });
    return;
  }

  // Auto-add: on by default, once per session, unless the customer opted out.
  const defaultOn = root.dataset.defaultOn === 'true';
  const dismissed = storeGet(PROTECT_OFF_KEY) === '1';
  const alreadyAuto = storeGet(PROTECT_AUTO_KEY) === '1';
  if (autoAdd && qty === 0 && defaultOn && !dismissed && !alreadyAuto && otherItems > 0) {
    storeSet(PROTECT_AUTO_KEY, '1');
    await applyProtection(true, { silent: true, cart });
    return;
  }

  // No mutation needed — but if the UI disagrees with the live cart, resync it.
  const shownOn =
    root.querySelector('[data-xcart-protect-toggle]')?.getAttribute('aria-checked') === 'true';
  if (shownOn !== qty > 0) {
    await resyncDrawer();
  }
}

/* ---------------------------------------------------------------- wiring */

/*
 * SINGLETON GUARD — the drawer section (and its <script> tag) is re-rendered
 * on every cart update, and after an asset edit the same script can get loaded
 * again under a new versioned URL. Without this guard, each copy would attach
 * its own click listener and every + / toggle click would fire multiple times
 * (the "added 2x" bug). Only the first loaded copy wires up; the rest no-op.
 */
if (!window.__xcartAddonsWired) {
  window.__xcartAddonsWired = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addBtn = target.closest('[data-xcart-add]');
    if (addBtn) {
      event.preventDefault();
      const deal = addBtn.closest('.xcart-deal');
      const select = deal?.querySelector('[data-xcart-variant]');
      addDeal(select ? select.value : addBtn.dataset.variantId, addBtn, addBtn.dataset.slot);
      return;
    }

    const upgradeBtn = target.closest('[data-xcart-upgrade]');
    if (upgradeBtn) {
      event.preventDefault();
      upgradeToSubscription(upgradeBtn);
      return;
    }

    const toggle = target.closest('[data-xcart-protect-toggle]');
    if (toggle) {
      event.preventDefault();
      onProtectToggle(toggle);
    }
  });

  // Drawer opened (covers PDP auto-open too — the open event bubbles to document).
  document.addEventListener('theme-drawer:open', (event) => {
    const drawer = event.target;
    if (drawer instanceof Element && drawer.id === 'cart-drawer') {
      setTimeout(reconcileProtection, 150);
    }
  });

  // After any cart lines update (ours or Horizon's own quantity buttons),
  // re-check protection state once the dust settles.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const anyEvent = /** @type {any} */ (event);
    // Product-page subscription direct checkout intentionally skips drawer-only add-ons.
    if (anyEvent.subscriptionDirectCheckout) return;
    const run = () => reconcileProtection();
    if (anyEvent.promise?.then) {
      anyEvent.promise.then(() => setTimeout(run, 150)).catch(() => {});
    } else {
      setTimeout(run, 300);
    }
  });

  // On every page load: clean up orphaned addon-only carts (no auto-add here —
  // that would pop the drawer open). If the drawer is already open (restored
  // navigation), run the full reconcile including auto-add.
  const onLoad = () => {
    const drawerOpen = Boolean(document.querySelector('#cart-drawer[open]'));
    console.info('[xcart]', window.__xcartVersion, 'load check (drawer open:', drawerOpen + ')');
    setTimeout(() => reconcileProtection({ autoAdd: drawerOpen }), 400);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad);
  } else {
    onLoad();
  }
}